import { ForbiddenApiErrorDialog } from "@/components/Error/ForbiddenApiErrorDialog";
import { GlobalErrorDialog } from "@/components/Error/GlobalErrorDialog";
import { ViewportLoader } from "@/components/Loading";
import { Toaster } from "@/components/ui/toaster";
import { initializeQRScannerWasm } from "@/features/qr-scanner/wasm-init";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "@tanstack/react-router";
import { Amplify } from "aws-amplify";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

import { env } from "@/lib/env/constants";
import { initializeAllEventListeners } from "@/lib/events/event-listeners";
import type { LanguageProvider } from "@/lib/i18n/adapters/i18next-init";
import { initializeLogger } from "@/lib/logger";
import { queryClient } from "@/lib/queryClient";
import { router } from "@/lib/router";

function initializeRootElement() {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }
  if (!rootElement.innerHTML) {
    const root = createRoot(rootElement);
    return root;
  }
  return null;
}

// ルート要素を事前に初期化
const root = initializeRootElement();

/**
 * i18nの初期化を実行
 * 認証ストアから言語設定を取得してi18nを設定
 */
async function initializeI18n() {
  const { initializeI18n: initI18n } = await import(
    "./lib/i18n/adapters/i18next-init"
  );

  const { useAuthStore } = await import("./lib/auth");

  try {
    const authStore = useAuthStore.getState();
    let languageProvider: LanguageProvider | undefined;
    const authLanguageSettings: LanguageProvider = {
      sessionLocale: authStore.session?.locale,
      clientLocale: authStore.locale,
    };

    if (!languageProvider) {
      languageProvider = authLanguageSettings;
    }

    // i18n初期化を実行
    await initI18n({}, languageProvider);
  } catch (error) {
    console.error("Failed to initialize i18n for app:", error);
    // エラーの場合もデフォルト設定でi18nを初期化
    await initI18n({});
  }
}

if (root) {
  root.render(<ViewportLoader />);
}

async function initializeApp() {
  try {
    // Amplifyの設定
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: env.VITE_COGNITO_USER_POOL_ID,
          userPoolClientId: env.VITE_COGNITO_PUBLIC_CLIENT_ID,
        },
      },
    });
    initializeLogger();
    initializeAllEventListeners();

    // QRスキャナーのWASM設定（BarcodeDetectorが使用される前に実行）
    initializeQRScannerWasm();

    // i18nの初期化
    await initializeI18n();

    if (root) {
      root.render(
        <StrictMode>
          <Suspense fallback={<ViewportLoader />}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              <Toaster />
              <GlobalErrorDialog />
              <ForbiddenApiErrorDialog />
              {/* React Query Devtools - 開発環境でのみ表示 */}
              <ReactQueryDevtools
                initialIsOpen={false}
                position="bottom"
                buttonPosition="bottom-right"
              />
            </QueryClientProvider>
          </Suspense>
        </StrictMode>,
      );
    }
  } catch (error) {
    console.error("Failed to initialize application:", error);
    if (root) {
      root.render(
        <div className="flex h-screen w-screen items-center justify-center text-red-600">
          Failed to initialize application. Please reload the page.
        </div>,
      );
    }
  }
}

initializeApp();
